import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import EmojiPicker, { Categories, EmojiClickData } from 'emoji-picker-react';

import { ChatSticker } from 'types/interfaces/chat/ChatSticker';
import { ChatStickerPack } from 'types/interfaces/chat/ChatStickerPack';

import { StickersApi } from 'api/StickersApi';
import {
  getRecentAssets,
  RecentAsset,
  updateRecentAssets,
} from 'helpers/chat-assets';
import { useStickersAnalytics } from 'hooks/analytics/useStickersAnalytics';
import { useStickersQuery } from 'hooks/queries/useStickersQuery';
import { useMedia } from 'hooks/useMedia';
import { useToast } from 'hooks/useToast';
import { getUser } from 'store/auth/selectors';

import { ReactComponent as EmojiSadIcon } from 'assets/icons/emoji-sad.svg';
import { ReactComponent as LockDarkIcon } from 'assets/icons/lock-dark.svg';
import { ReactComponent as LockLightIcon } from 'assets/icons/lock-light.svg';
import { ReactComponent as EmojiNavIcon } from 'assets/icons/nav-emoji.svg';
import { ReactComponent as GifNavIcon } from 'assets/icons/nav-gif.svg';
import { ReactComponent as StickerNavIcon } from 'assets/icons/nav-sticker.svg';
import { ReactComponent as RecentIcon } from 'assets/icons/recent-assets.svg';

import { BaseButton, ButtonTypes } from 'components/base/BaseButton';
import { Spinner, SpinnerTypes } from 'components/base/Spinner';

import { StickerLoader } from '../StickerLoader';

import css from './assetsList.module.sass';

const EMOJI_CATEGORIES = [
  {
    category: Categories.SUGGESTED,
    name: 'Frequently Used',
  },
  {
    category: Categories.SMILEYS_PEOPLE,
    name: 'Smileys & People',
  },
];

enum AssetsTabs {
  Emojis,
  Gifs,
  Stickers,
}

type PropTypes = {
  shownStickerPackId: number;
  selectShownStickerPackId?: (packId: number) => void;
  handleInsertEmoji: (emoji: string) => void;
  onChange: (payload: {
    sticker: ChatSticker;
    stickerPack: ChatStickerPack;
    onSuccessPayment: () => void;
  }) => void;
  onRecentStickerSent?: (sticker: ChatSticker) => void;
};

export const AssetsList: React.FC<PropTypes> = memo(
  ({
    shownStickerPackId,
    selectShownStickerPackId,
    handleInsertEmoji,
    onChange,
    onRecentStickerSent,
  }) => {
    const { t } = useTranslation('app');

    const { isMobile } = useMedia();
    const { showErrorToast } = useToast();

    const [stickerPacks, setStickerPacks] = useState<ChatStickerPack[]>([]);
    const [selectedStickerPack, setSelectedStickerPack] =
      useState<ChatStickerPack | null>(null);

    const [assetTab, setAssetTab] = useState(AssetsTabs.Stickers);
    const [isAssetTabInitialized, setIsAssetTabInitialized] = useState(false);

    const [isSelectedRecentAssets, setIsSelectedRecentAssets] = useState(false);
    const [recentAssets, setRecentAssets] =
      useState<RecentAsset[]>(getRecentAssets);

    const user = useSelector(getUser);

    const {
      refetch: refetchStickers,
      data: stickersData,
      isLoading: isStickersLoading,
      isRefetching: isStickersRefetching,
    } = useStickersQuery(
      { packId: selectedStickerPack?.id || 0 },
      {
        enabled: false,
        onError: () => {
          showErrorToast({
            title: t('thread.sticker.loadFail'),
          });
        },
      }
    );

    const { trackClickOnAssetTab } = useStickersAnalytics();

    const isStickersLoaded = useMemo(
      () =>
        !isStickersLoading &&
        !isStickersRefetching &&
        !!stickersData &&
        !!stickerPacks.length,
      [
        isStickersLoading,
        isStickersRefetching,
        stickerPacks.length,
        stickersData,
      ]
    );

    const isGifsListNotEmpty = useMemo(
      () => !!stickerPacks.filter((packItem) => packItem.type === 'gif').length,
      [stickerPacks]
    );

    const handleEmojisTabClick = useCallback(() => {
      setAssetTab(AssetsTabs.Emojis);
      trackClickOnAssetTab({ info: 'message_emoji' });
    }, [trackClickOnAssetTab]);

    const activateRecentAssets = useCallback(() => {
      if (recentAssets.length) {
        setIsSelectedRecentAssets(true);
      }
    }, [recentAssets.length]);

    const handleRecentAssetsClick = useCallback(() => {
      setIsSelectedRecentAssets(true);
      setSelectedStickerPack(null);
      selectShownStickerPackId && selectShownStickerPackId(-1);

      setRecentAssets(getRecentAssets);
    }, [selectShownStickerPackId]);

    const handleRecentAssetsUpdate = useCallback((asset: ChatSticker) => {
      const updatedRecentAssets = updateRecentAssets(asset);

      setRecentAssets(updatedRecentAssets);
    }, []);

    const handleGifsTabClick = useCallback(() => {
      setAssetTab(AssetsTabs.Gifs);
      trackClickOnAssetTab({ info: 'message_gif' });
    }, [trackClickOnAssetTab]);

    const handleStickersTabClick = useCallback(() => {
      setAssetTab(AssetsTabs.Stickers);
      trackClickOnAssetTab({ info: 'message_sticker' });
    }, [trackClickOnAssetTab]);

    const handleStickerPackClick = useCallback(
      (stickerPack: ChatStickerPack) => {
        setSelectedStickerPack(stickerPack);
        selectShownStickerPackId && selectShownStickerPackId(stickerPack.id);
        setIsSelectedRecentAssets(false);
      },
      [selectShownStickerPackId]
    );

    const handleSuccessStickerPurchase = useCallback(
      (boughtStickerPackId: number) => {
        setStickerPacks((prevStickerPacks) => {
          return prevStickerPacks.map((prevStickerPack) => {
            if (prevStickerPack.id === boughtStickerPackId)
              return { ...prevStickerPack, is_bought: true };

            return prevStickerPack;
          });
        });

        setSelectedStickerPack((prev) => {
          if (prev) {
            return { ...prev, is_bought: true };
          }
          return prev;
        });
      },
      []
    );

    const handleStickerClick = useCallback(
      (sticker: ChatSticker) => {
        if (!selectedStickerPack || !sticker) {
          showErrorToast({
            title: t('thread.sticker.sendFailTitle'),
            subtitle: t('thread.sticker.sendFailSubtitle'),
          });

          return;
        }

        if (
          selectedStickerPack.is_bought ||
          (user?.is_premium && selectedStickerPack.is_free)
        ) {
          handleRecentAssetsUpdate(sticker);
        }

        onChange({
          sticker,
          stickerPack: selectedStickerPack,
          onSuccessPayment: () => {
            handleSuccessStickerPurchase(selectedStickerPack.id);
          },
        });
      },
      [
        handleRecentAssetsUpdate,
        handleSuccessStickerPurchase,
        onChange,
        selectedStickerPack,
        showErrorToast,
        t,
        user?.is_premium,
      ]
    );

    const handleRecentStickerClick = useCallback(
      (sticker: ChatSticker) => {
        if (!sticker || !onRecentStickerSent) {
          showErrorToast({
            title: t('thread.sticker.sendFailTitle'),
            subtitle: t('thread.sticker.sendFailSubtitle'),
          });

          return;
        }

        handleRecentAssetsUpdate(sticker);
        onRecentStickerSent(sticker);
      },
      [handleRecentAssetsUpdate, onRecentStickerSent, showErrorToast, t]
    );

    const handleEmojiClick = useCallback(
      (data: EmojiClickData) => {
        handleInsertEmoji(data.emoji);
      },
      [handleInsertEmoji]
    );

    const stickersPacksByAssetsTab = useMemo(() => {
      if (assetTab === AssetsTabs.Gifs)
        return stickerPacks.filter((packItem) => packItem.type === 'gif');

      return stickerPacks.filter((packItem) => packItem.type === 'image');
    }, [assetTab, stickerPacks]);

    useEffect(() => {
      if (shownStickerPackId === -1 && !selectedStickerPack) {
        activateRecentAssets();
      }
    }, [activateRecentAssets, selectedStickerPack, shownStickerPackId]);

    // open sticker pack by click on sticker message in chat
    useEffect(() => {
      if (shownStickerPackId === -1) return;

      const selectedPack =
        stickerPacks.find((d) => d.id === shownStickerPackId) ||
        stickerPacks[0];

      setAssetTab(
        selectedPack?.type === 'gif' ? AssetsTabs.Gifs : AssetsTabs.Stickers
      );

      selectedPack && handleStickerPackClick(selectedPack);
    }, [handleStickerPackClick, shownStickerPackId, stickerPacks]);

    useEffect(() => {
      if (
        !selectedStickerPack &&
        stickerPacks.length &&
        shownStickerPackId !== -1 &&
        !isSelectedRecentAssets
      ) {
        const selectedPack =
          stickerPacks.find((d) => d.id === shownStickerPackId) ||
          stickerPacks[0];

        setAssetTab(
          selectedPack?.type === 'gif' ? AssetsTabs.Gifs : AssetsTabs.Stickers
        );
        setIsAssetTabInitialized(true);
      }
    }, [
      isSelectedRecentAssets,
      selectedStickerPack,
      shownStickerPackId,
      stickerPacks,
    ]);

    useEffect(() => {
      if (
        !isSelectedRecentAssets &&
        stickersPacksByAssetsTab.length &&
        (isAssetTabInitialized || shownStickerPackId === -1)
      ) {
        const selectedPack =
          stickersPacksByAssetsTab.find((d) => d.id === shownStickerPackId) ||
          stickersPacksByAssetsTab[0];

        if (
          (selectedStickerPack?.type === 'image' &&
            assetTab === AssetsTabs.Stickers) ||
          (selectedStickerPack?.type === 'gif' && assetTab === AssetsTabs.Gifs)
        )
          return;

        setSelectedStickerPack(selectedPack);
      }
    }, [
      assetTab,
      isAssetTabInitialized,
      isSelectedRecentAssets,
      selectedStickerPack?.type,
      shownStickerPackId,
      stickersPacksByAssetsTab,
    ]);

    useEffect(() => {
      const localFetchStickerPacks = async () => {
        try {
          const stickerPacksData = await StickersApi.fetchStickerPacks();

          setStickerPacks(stickerPacksData);
        } catch (err) {
          showErrorToast({
            title: t('thread.sticker.loadFail'),
          });
        }
      };

      localFetchStickerPacks();
    }, [showErrorToast, t]);

    useEffect(() => {
      if (
        !selectedStickerPack ||
        isSelectedRecentAssets ||
        selectedStickerPack.id === stickersData?.[0]?.sticker_pack_id
      ) {
        return;
      }

      refetchStickers();
    }, [
      isSelectedRecentAssets,
      refetchStickers,
      selectedStickerPack,
      stickersData,
    ]);

    return (
      <div className={css.root}>
        {assetTab !== AssetsTabs.Emojis && (
          <div className={css.container}>
            <ul className={css.stickerPackList}>
              <li
                className={cx(css.stickerPackListItem, {
                  [css.stickerPackListItemActive]: isSelectedRecentAssets,
                })}
                onClick={handleRecentAssetsClick}
                key="recent-assets"
              >
                <BaseButton
                  type={ButtonTypes.Transparent}
                  onClick={handleRecentAssetsClick}
                  className={css.stickerPackListItemBtn}
                >
                  <RecentIcon className={css.recentIcon} />
                </BaseButton>
              </li>

              {stickersPacksByAssetsTab.map((stickerPack) => (
                <li
                  className={cx(css.stickerPackListItem, {
                    [css.stickerPackListItemActive]:
                      stickerPack.id === selectedStickerPack?.id &&
                      !isSelectedRecentAssets,
                  })}
                  key={stickerPack.id}
                >
                  <BaseButton
                    type={ButtonTypes.Transparent}
                    onClick={() => handleStickerPackClick(stickerPack)}
                    className={css.stickerPackListItemBtn}
                  >
                    <img
                      src={stickerPack.big_url || stickerPack.preview_url}
                      alt={stickerPack.name}
                      className={cx(css.stickerPackImg, {
                        [css.stickerPackImgLocked]:
                          !stickerPack?.is_bought && !stickerPack?.is_free,
                      })}
                    />
                    {!stickerPack?.is_bought && !stickerPack?.is_free && (
                      <>
                        <div className={css.stickerPackLockHover} />
                        <LockDarkIcon className={css.stickerPackLockIcon} />
                      </>
                    )}
                  </BaseButton>
                </li>
              ))}
            </ul>

            {isStickersLoaded && !isSelectedRecentAssets && (
              <div className={css.stickersGridWrapper}>
                <ul className={css.stickersGrid}>
                  {stickersData?.map((sticker) => (
                    <li key={sticker.id} className={css.stickersListItem}>
                      <BaseButton
                        className={css.stickersListItemBtn}
                        type={ButtonTypes.Transparent}
                        onClick={() => {
                          handleStickerClick(sticker);
                        }}
                      >
                        <StickerLoader
                          src={sticker.big_url || sticker.preview_url}
                        />
                        {!selectedStickerPack?.is_bought &&
                          !selectedStickerPack?.is_free && (
                            <LockLightIcon className={css.stickerLockIcon} />
                          )}
                      </BaseButton>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isSelectedRecentAssets && !!recentAssets?.length && (
              <div className={css.stickersGridWrapper}>
                <ul className={css.stickersGrid}>
                  {recentAssets?.map(({ sticker }) => (
                    <li key={sticker.id} className={css.stickersListItem}>
                      <BaseButton
                        className={css.stickersListItemBtn}
                        type={ButtonTypes.Transparent}
                        onClick={() => {
                          handleRecentStickerClick(sticker);
                        }}
                      >
                        <StickerLoader
                          src={sticker.big_url || sticker.preview_url}
                        />
                      </BaseButton>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isSelectedRecentAssets && !recentAssets?.length && (
              <div className={css.stickersWrapperEmpty}>
                <EmojiSadIcon className={css.emptyStickerIcon} />
                <p>You haven’t sent any stickers yet</p>
              </div>
            )}

            {!isStickersLoaded && !isSelectedRecentAssets && (
              <div className={css.spinner}>
                <Spinner type={SpinnerTypes.Dark} />
              </div>
            )}
          </div>
        )}

        {assetTab === AssetsTabs.Emojis && (
          <EmojiPicker
            emojiVersion="5.0"
            categories={EMOJI_CATEGORIES}
            searchDisabled
            lazyLoadEmojis
            width="100%"
            height={`calc(${isMobile ? 40 : 30}vh - 42px)`}
            previewConfig={{
              showPreview: false,
            }}
            onEmojiClick={handleEmojiClick}
          />
        )}

        <div className={css.assetsNavigation}>
          <BaseButton
            className={cx(css.navBtn, {
              [css.navBtnActive]: assetTab === AssetsTabs.Emojis,
            })}
            type={ButtonTypes.Transparent}
            onClick={handleEmojisTabClick}
          >
            <EmojiNavIcon />
          </BaseButton>
          {isGifsListNotEmpty && (
            <BaseButton
              className={cx(css.navBtn, {
                [css.navBtnActive]: assetTab === AssetsTabs.Gifs,
              })}
              type={ButtonTypes.Transparent}
              onClick={handleGifsTabClick}
            >
              <GifNavIcon />
            </BaseButton>
          )}
          <BaseButton
            className={cx(css.navBtn, {
              [css.navBtnActive]: assetTab === AssetsTabs.Stickers,
            })}
            type={ButtonTypes.Transparent}
            onClick={handleStickersTabClick}
          >
            <StickerNavIcon />
          </BaseButton>
        </div>
      </div>
    );
  }
);
